import React from 'react';
import type { Todo } from '@ext-bookmark/shared/types';
import { getRecurrenceTypeLabel } from '../../../services/todo/recurrence';
import { toggleTodoComplete, getBaseTodoId } from '../../../services/todo/storage';

interface MobileTodoListProps {
  selectedDate: Date | null;
  todos: Todo[];
  onTodoClick: (todo: Todo) => void;
  onDateDoubleClick: (date: Date) => void;
  onDateClick: (date: Date) => void;
  onTodosChange: (todos: Todo[]) => void;
  /** 勾选成功后调用，用于使 React Query 失效（仅错误路径）；成功路径改用 onTodoUpdated 局部更新 */
  onTodosInvalidate?: () => void;
  /** 勾选成功后传入更新后的 todo，用于父组件局部更新 React Query 缓存 */
  onTodoUpdated?: (updatedTodo: Todo) => void;
  /** 已登录时传入：切换时实时获取最新 todos，避免闭包滞后 */
  getCurrentTodosForToggle?: () => Todo[] | undefined;
  /** @deprecated 使用 getCurrentTodosForToggle */
  currentTodosForToggle?: Todo[] | null;
  /** 勾选时立即乐观更新 React Query 缓存 */
  onOptimisticToggle?: (todoId: string, newCompleted: boolean, instanceDate?: number) => void;
}

export const MobileTodoList: React.FC<MobileTodoListProps> = ({
  selectedDate,
  todos,
  onTodoClick,
  onDateDoubleClick,
  onDateClick: _onDateClick,
  onTodosChange,
  onTodosInvalidate,
  onTodoUpdated,
  getCurrentTodosForToggle,
  currentTodosForToggle,
  onOptimisticToggle,
}) => {
  const handleTodoToggle = async (e: React.MouseEvent, todo: Todo) => {
    e.stopPropagation();
    e.preventDefault();
    
    const originalTodoId = getBaseTodoId(todo);
    const isRecurrenceInstance = !!todo.recurrence && todo.id !== originalTodoId;
    const instanceDate = isRecurrenceInstance ? todo.startDate : undefined;
    
    const newCompleted = !todo.completed;
    
    // 立即乐观更新（已登录时更新 React Query 缓存，未登录时更新本地）
    onOptimisticToggle?.(originalTodoId, newCompleted, instanceDate);
    const updatedTodos = todos.map(t =>
      t.id === todo.id ? { ...t, completed: newCompleted } : t
    );
    onTodosChange?.(updatedTodos);
    
    // 异步保存到存储，成功后仅局部更新，不整块 invalidate
    try {
      const updated = await toggleTodoComplete(originalTodoId, instanceDate, {
        getCurrentTodos: getCurrentTodosForToggle,
        currentTodos: getCurrentTodosForToggle ? undefined : (currentTodosForToggle ?? undefined),
        currentInstanceCompleted: todo.completed,
      });
      if (updated) {
        const normalizedInstance = instanceDate !== undefined
          ? new Date(instanceDate).setHours(0, 0, 0, 0)
          : undefined;
        const completed = updated.recurrence && normalizedInstance !== undefined
          ? (updated.recurrence.completedDates || []).includes(normalizedInstance)
          : !!updated.completed;
        onTodosChange(todos.map(t => t.id !== todo.id ? t : { ...t, completed, recurrence: updated.recurrence ?? t.recurrence }));
        onTodoUpdated?.(updated);
      }
    } catch (error) {
      console.error('[MobileTodoList] 切换待办事项状态失败:', error);
      onTodosChange(todos);
      onTodosInvalidate?.();
    }
  };

  if (!selectedDate) {
    return (
      <div className="text-center py-8">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          请选择一个日期查看待办事项
        </p>
      </div>
    );
  }

  if (todos.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          这一天没有待办事项
        </p>
        <button
          onClick={() => selectedDate && onDateDoubleClick?.(selectedDate)}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium shadow-sm hover:shadow-md transition-all ring-2 ring-primary/30"
          aria-label="添加待办事项"
        >
          <svg className="w-5 h-5 text-primary-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          添加待办
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-3 sticky top-0 bg-white dark:bg-gray-800 py-2 z-10">
        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {selectedDate.toLocaleDateString('zh-CN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            weekday: 'long',
          })}
        </div>
        {/* 新增待办事项按钮 */}
        <button
          onClick={() => {
            if (selectedDate && onDateDoubleClick) {
              onDateDoubleClick(selectedDate);
            }
          }}
          className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground shadow-md hover:shadow-lg transition-all duration-200 hover:scale-110 active:scale-95 ring-2 ring-primary/30"
          aria-label="添加待办事项"
          title="添加待办事项"
        >
          <svg
            className="w-6 h-6 text-primary-foreground"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 4v16m8-8H4"
            />
          </svg>
        </button>
      </div>
      <div className="space-y-2">
        {todos.map((todo) => {
          const recurrenceLabel = todo.recurrence 
            ? getRecurrenceTypeLabel(todo.recurrence.type, todo.recurrence)
            : null;

          return (
            <div
              key={todo.id}
              className="flex items-start gap-2 p-2 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              onClick={() => onTodoClick(todo)}
            >
              <button
                type="button"
                onClick={(e) => handleTodoToggle(e, todo)}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                onTouchStart={(e) => {
                  e.stopPropagation();
                }}
                className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                  todo.completed
                    ? 'bg-primary border-primary shadow-sm'
                    : 'bg-transparent border-gray-300 dark:border-gray-500 hover:border-primary/50'
                }`}
                aria-label={todo.completed ? '标记为未完成' : '标记为完成'}
              >
                {todo.completed && (
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                )}
              </button>
              <div className="flex-1 min-w-0">
                <div
                  className={`text-xs font-medium mb-0.5 ${
                    todo.completed
                      ? 'line-through text-gray-400 dark:text-gray-500'
                      : 'text-gray-900 dark:text-gray-100'
                  }`}
                  style={{ color: todo.fontColor && !todo.completed ? todo.fontColor : undefined }}
                >
                  {todo.title}
                </div>
                {todo.description && (
                  <div className={`text-xs text-gray-600 dark:text-gray-400 ${
                    todo.completed ? 'line-through' : ''
                  }`}>
                    {todo.description}
                  </div>
                )}
                {(recurrenceLabel || todo.reminder?.enabled) && (
                  <div className="flex flex-wrap items-center gap-1.5 mt-1 text-[10px] text-gray-500 dark:text-gray-400">
                    {recurrenceLabel && (
                      <span>{recurrenceLabel}</span>
                    )}
                    {todo.reminder?.enabled && todo.reminder.minutesBefore && todo.reminder.minutesBefore.length > 0 && (
                      <span>
                        提醒: {todo.reminder.minutesBefore.map(m => {
                          const absM = Math.abs(m);
                          if (absM >= 1440) return `提前${Math.floor(absM / 1440)}天`;
                          if (absM >= 60) return `提前${Math.floor(absM / 60)}小时`;
                          return `提前${absM}分钟`;
                        }).join(', ')}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};
